import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import { storage } from "../../firebase";
import FemaleLevelFrame from "../FemaleLevelFrame/FemaleLevelFrame";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";
import { formatNumberEn } from "../../utils/digits";

/** مسافة أعلى الهيدر — تُضبط من JS عبر femaleLevelHeroTopSpacer + --female-hero-top-spacer */
const FEMALE_HERO_SPACER_PX = 28;

function isHttpUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  if (value.startsWith("gs://")) {
    const marker = ".appspot.com/";
    const idx = value.indexOf(marker);
    return idx !== -1
      ? value.slice(idx + marker.length)
      : value.replace(/^gs:\/\/[^/]+\//, "");
  }

  if (value.includes("/o/")) {
    try {
      const encodedPath = value.split("/o/")[1]?.split("?")[0] || "";
      return decodeURIComponent(encodedPath);
    } catch {
      return "";
    }
  }

  return value;
}

export default function FemaleLevelHero({
  profile,
  displayLevel,
  progressData,
}) {
  const { t } = useTranslation();
  const [resolvedImageUrl, setResolvedImageUrl] = useState(() =>
    defaultAvatarByGender("female")
  );

  const isLv0 = Number(progressData?.currentLevel ?? 0) === 0;
  const isMaxLevel = Boolean(progressData?.isMaxLevel);
  const needsDays = Number(progressData?.requiredDays ?? 0) > 0;

  const rawImageValue =
    profile?.photoURL ||
    profile?.avatar ||
    profile?.profileImage ||
    profile?.mainPhotoURL ||
    "";

  useEffect(() => {
    let cancelled = false;

    async function resolveImage() {
      try {
        if (!rawImageValue) {
          if (!cancelled) setResolvedImageUrl(defaultAvatarByGender("female"));
          return;
        }

        if (isHttpUrl(rawImageValue)) {
          if (!cancelled) setResolvedImageUrl(rawImageValue);
          return;
        }

        const path = storagePathFromValue(rawImageValue);

        if (!path) {
          if (!cancelled) setResolvedImageUrl(defaultAvatarByGender("female"));
          return;
        }

        const url = await getDownloadURL(storageRef(storage, path));
        if (!cancelled) setResolvedImageUrl(url);
      } catch (error) {
        console.error("❌ FemaleLevelHero image resolve error:", error);
        if (!cancelled) setResolvedImageUrl(defaultAvatarByGender("female"));
      }
    }

    resolveImage();

    return () => {
      cancelled = true;
    };
  }, [rawImageValue]);

  return (
    <div
      className="femaleLevelHero"
      style={{ "--female-hero-top-spacer": `${FEMALE_HERO_SPACER_PX}px` }}
    >
      <div
        className="femaleLevelHeroTopSpacer"
        style={{ height: FEMALE_HERO_SPACER_PX }}
        aria-hidden
      />

      <div className="femaleLevelHeroTop">
        <FemaleLevelFrame
          level={displayLevel}
          imageUrl={resolvedImageUrl}
          size={190}
        />
      </div>

      <div className="femaleLevelHeroProgressText">
        <strong>
          {formatNumberEn(progressData.cycleTokens)} /{" "}
          {formatNumberEn(progressData.targetTokens)}
        </strong>

        <span>
          {isMaxLevel
            ? t("female_level.status.maxLevelReached", {
                defaultValue: "لقد وصلتِ إلى أعلى مستوى",
              })
            : t("female_level.progress.toUpgrade", {
                defaultValue: "توكن للترقية",
              })}
        </span>
      </div>

      <div className="femaleLevelBar">
        <div
          className="femaleLevelBarFill"
          style={{ width: `${progressData.tokenProgressPercent}%` }}
        />
      </div>

      <div className="femaleLevelHeroMeta">
        {isMaxLevel ? (
          <div className="femaleHeroMetaItem">
            <span>👑</span>
            <p>
              {t("female_level.status.maxLevelHint", {
                defaultValue: "أنتِ الآن في أعلى مستوى متاح",
              })}
            </p>
          </div>
        ) : isLv0 ? (
          <div className="femaleHeroMetaItem">
            <span>🔥</span>
            <p>
              {t("female_level.requirements.levelZeroRule", {
                defaultValue: "المستوى صفر يعتمد فقط على كمية التوكن",
              })}
            </p>
          </div>
        ) : (
          <>
            {needsDays && (
              <div className="femaleHeroMetaItem">
                <span>🔥</span>
                <p>
                  {t("female_level.progress.activeDaysLine", {
                    defaultValue: "اشتغلتِ {{active}} من {{required}} أيام",
                    active: formatNumberEn(progressData.activeDays),
                    required: formatNumberEn(progressData.requiredDays),
                  })}
                </p>
              </div>
            )}

            {needsDays && (
              <div className="femaleHeroMetaItem">
                <span>⏳</span>
                <p>
                  {t("female_level.progress.daysLeftLine", {
                    defaultValue: "باقي {{days}} أيام",
                    days: formatNumberEn(progressData.daysLeft),
                  })}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="femaleLevelHeroHint">
        {isMaxLevel
          ? t("female_level.status.maxLevelDone", {
              defaultValue: "تم الوصول إلى الحد الأعلى للمستويات",
            })
          : progressData.isReady
          ? t("female_level.status.readyNow", {
              defaultValue: "أنتِ جاهزة الآن للمستوى التالي",
            })
          : t("female_level.progress.remainingTokensLine", {
              defaultValue: "تحتاجين {{count}} توكن إضافي للترقية",
              count: formatNumberEn(progressData.remainingTokens),
            })}
      </div>
    </div>
  );
}